import type { Message } from '../types/chat';

const DAY_MS = 24 * 60 * 60 * 1000;
// A gap larger than this between two consecutive messages opens a new session
// (i.e. emits a fresh centered timestamp divider above the next message).
// 4 hours, not Apple's ~15 minutes: see NS-8 in docs/DESIGN_SYSTEM.md. A
// calendar-day change opens a session regardless of the gap.
const SESSION_GAP_MS = 4 * 60 * 60 * 1000;

export interface MessageSession {
  /** Pre-formatted header label rendered as the centered session divider. */
  header: string;
  /** Ordered messages belonging to this session. */
  messages: Message[];
}

/**
 * Format a timestamp for the centered session-divider header.
 *
 *   Today (same calendar day):       "Today 9:43 AM"
 *   Yesterday (1 day back):          "Yesterday 2:15 PM"
 *   Within last 7 days:              "Tuesday 11:30 AM"
 *   Older:                           "Sep 1, 2026 4:56 PM"
 *
 * Older dates always carry the year, including the current one. A divider is
 * the only date context in the thread, so "Feb 21" three screens up is
 * ambiguous in a way "Feb 21, 2026" is not, and the async cadence here means
 * threads routinely span a year.
 *
 * `now` is injectable for deterministic testing.
 */
export function formatSessionTimestamp(date: Date, now: Date = new Date()): string {
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  // Compare by calendar day, not raw ms — sidesteps DST edges.
  const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.floor((dayStart(now) - dayStart(date)) / DAY_MS);

  if (dayDiff === 0) return `Today ${time}`;
  if (dayDiff === 1) return `Yesterday ${time}`;
  if (dayDiff > 1 && dayDiff < 7) {
    return `${date.toLocaleDateString('en-US', { weekday: 'long' })} ${time}`;
  }
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ${time}`;
}

/**
 * Format the per-bubble hover-reveal label — just the clock time.
 * Day context is already conveyed by the session header above the group.
 */
export function formatExactTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/**
 * Smart age-aware timestamp for the inbox row metadata. Mirrors the
 * iMessage / WhatsApp pattern: recent activity reads as "now" / "Xm",
 * the rest of today reads as the time of day, and older messages
 * collapse progressively to "Yesterday" / weekday / month-day / dated.
 *
 * Rules (first match wins):
 *   < 60s           → "now"
 *   < 60min         → "5m"
 *   same day, ≥60m  → "10:24 AM"
 *   yesterday       → "Yesterday"
 *   < 7 days        → "Tuesday"
 *   same year       → "Feb 21"
 *   different year  → "Feb 21, 2024"
 *
 * Future-dated input (clock skew, server-time drift) is treated as "now".
 * Null / undefined / invalid input returns the empty string so the call
 * site can render it directly without guarding.
 *
 * `now` is injectable for deterministic testing.
 */
export function formatInboxTimestamp(
  input: Date | string | null | undefined,
  now: Date = new Date(),
): string {
  if (input === null || input === undefined || input === '') return '';
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = now.getTime() - date.getTime();
  // Future dates collapse to "now" — covers clock skew, optimistic UI
  // writes with serverTimestamp() not yet committed, etc.
  if (diffMs < 60_000) return 'now';

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m`;

  // Compare by calendar day to sidestep DST edges.
  const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.floor((dayStart(now) - dayStart(date)) / DAY_MS);

  if (dayDiff === 0) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Group a chronologically ordered list of messages into sessions.
 * A new session starts when:
 *   - the gap to the previous message exceeds SESSION_GAP_MS, OR
 *   - the calendar day changes.
 * Each session's header reflects the first message's createdAt.
 */
export function groupMessagesIntoSessions(messages: Message[]): MessageSession[] {
  if (messages.length === 0) return [];
  const sessions: MessageSession[] = [];
  let current: MessageSession | null = null;
  let prevDate: Date | null = null;

  for (const msg of messages) {
    const date = new Date(msg.createdAt);
    const sameDay =
      prevDate !== null &&
      date.getFullYear() === prevDate.getFullYear() &&
      date.getMonth() === prevDate.getMonth() &&
      date.getDate() === prevDate.getDate();
    const startsNew =
      !current ||
      !prevDate ||
      !sameDay ||
      date.getTime() - prevDate.getTime() > SESSION_GAP_MS;

    if (startsNew) {
      current = { header: formatSessionTimestamp(date), messages: [msg] };
      sessions.push(current);
    } else {
      current!.messages.push(msg);
    }
    prevDate = date;
  }
  return sessions;
}
